import {
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';

import {
  FunctionInvokeError,
  isFunctionFetchError,
  isFunctionInvokeAbortError,
} from '@/lib/functionInvokeErrors';

const TRANSIENT_HTTP_STATUSES = new Set([502, 503, 504]);
const SCHEMA_CACHE_RELOAD_PATTERN = /PGRST002|PGRST20[0-5]|schema cache/i;
const NETWORK_FAILURE_PATTERN = /failed to fetch|load failed|networkerror when attempting to fetch/i;
const EDGE_FUNCTION_NO_RESPONSE_PATTERN = /failed to send a request to the edge function/i;
// ENT-4764: postgrest-js (non-throwing mode) converts a rejected fetch into a plain
// `{ message: `${name}: ${message}`, code: '' }` error, so the TypeError instance is lost.
// Match only that exact `<Name>: <network failure>` prefix shape, never a free-text mention.
const POSTGREST_WRAPPED_NETWORK_FAILURE_PATTERN =
  /^(?:TypeError|FetchError): (?:failed to fetch|load failed|networkerror when attempting to fetch)/i;

function getPostgrestErrorText(error: Record<string, unknown>): string {
  return ['code', 'errorCode', 'message', 'details', 'hint', 'responseBody']
    .map((field) => error[field])
    .filter((value): value is string => typeof value === 'string')
    .join(' ');
}

function getHttpStatus(error: unknown): number | undefined {
  if (error instanceof FunctionInvokeError) {
    return error.status;
  }

  if (
    (error instanceof FunctionsHttpError || error instanceof FunctionsRelayError) &&
    error.context instanceof Response
  ) {
    return error.context.status;
  }

  return undefined;
}

function isNoResponseFunctionInvokeTransportError(error: unknown): error is FunctionInvokeError {
  return error instanceof FunctionInvokeError
    && error.status === undefined
    && error.responseBody === undefined
    && error.responseJson === undefined
    && (
      error.errorCode === 'edge_transport_failure'
      || EDGE_FUNCTION_NO_RESPONSE_PATTERN.test(error.message)
    );
}

export function isTransientBackendError(error: unknown): boolean {
  if (isFunctionInvokeAbortError(error)) {
    return false;
  }

  if (isFunctionFetchError(error)) {
    return true;
  }

  if (isNoResponseFunctionInvokeTransportError(error)) {
    return true;
  }

  if (error instanceof FunctionsRelayError) {
    return true;
  }

  const status = getHttpStatus(error);
  if (status !== undefined && TRANSIENT_HTTP_STATUSES.has(status)) {
    return true;
  }

  if (error instanceof FunctionInvokeError && status === 500) {
    const body = `${error.errorCode ?? ''} ${error.responseBody ?? ''}`;
    if (SCHEMA_CACHE_RELOAD_PATTERN.test(body)) {
      return true;
    }
  }

  if (
    error &&
    typeof error === 'object' &&
    'code' in error
  ) {
    const postgrestText = getPostgrestErrorText(error as Record<string, unknown>);
    if (SCHEMA_CACHE_RELOAD_PATTERN.test(postgrestText)) {
      return true;
    }
  }

  return error instanceof TypeError && NETWORK_FAILURE_PATTERN.test(error.message);
}

/**
 * ENT-4764: a postgrest-js network failure surfaced in non-throwing mode. Deliberately
 * NOT part of `isTransientBackendError`: postgrest-js already retries GET transport
 * failures internally, so widening the shared classifier would multiply those retries
 * through the global QueryClient policy (ENT-3312 class). Opt in per read site —
 * `retryTransientRead` uses it for single-shot POST RPC reads.
 */
export function isPostgrestWrappedNetworkFailure(error: unknown): boolean {
  if (isFunctionInvokeAbortError(error)) {
    return false;
  }
  if (!error || typeof error !== 'object' || !('message' in error)) {
    return false;
  }
  const { message } = error as { message?: unknown; code?: unknown };
  const code = (error as { code?: unknown }).code;
  // A real PostgREST/Postgres error carries a non-empty code; a wrapped fetch failure has none.
  if (typeof code === 'string' && code.length > 0) {
    return false;
  }
  return typeof message === 'string' && POSTGREST_WRAPPED_NETWORK_FAILURE_PATTERN.test(message);
}

export const TRANSIENT_READ_MAX_RETRIES = 3;

export function queryRetryPolicy(failureCount: number, error: unknown): boolean {
  return isTransientBackendError(error)
    ? failureCount < TRANSIENT_READ_MAX_RETRIES
    : failureCount < 1;
}
